"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar.jsx";
import Footer from "../../../../../components/footer.jsx";
import Button from "../../../../../components/Button.jsx";
import Table from "../../../../../components/Table.jsx";
import Pagination from "../../../../../components/Pagination.jsx";
import Modal from "../../../../../components/Modal.jsx";
import ConfirmPaymentModal from "../../../components/ConfirmPaymentModal.jsx";
import PaymentCompleteModal from "../../../components/PaymentCompleteModal.jsx";
import { Search } from "lucide-react";
import styles from "../css/creditProfiles.module.css";

const MOCK_PROFILES = [
  {
    profileNumber: "639000",
    surname: "Morris",
    initial: "T",
    title: "Mrs",
    address: "189 Oak Lane",
  },
  {
    profileNumber: "796000",
    surname: "Morris",
    initial: "L",
    title: "Mr",
    address: "4 Park Road",
  },
  {
    profileNumber: "945000",
    surname: "Taylor",
    initial: "M",
    title: "Ms",
    address: "89 Samson Street",
  },
  {
    profileNumber: "180000",
    surname: "Fernandez",
    initial: "B",
    title: "Miss",
    address: "21 Blundall Road",
  },
  {
    profileNumber: "240000",
    surname: "Nguyen",
    initial: "K",
    title: "Mr",
    address: "12 Palm Grove",
  },
  {
    profileNumber: "310000",
    surname: "Singh",
    initial: "R",
    title: "Ms",
    address: "7 Willow Street",
  },
  {
    profileNumber: "520000",
    surname: "Moyo",
    initial: "J",
    title: "Mrs",
    address: "3 Cedar Close",
  },
];

const MOCK_DEPENDENTS = [
  { surname: "Morris", initial: "T", title: "Mrs", role: "Main Member" },
  { surname: "Morris", initial: "L", title: "Mr", role: "Dependent" },
  { surname: "Morris", initial: "M", title: "Ms", role: "Dependent" },
];

export default function CreditProfilesView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [total, setTotal] = useState(0);
  const pageSize = 5;

  const fromSplit = !!(location.state && location.state.splitFlow);
  const fromDebtors = !!(location.state && location.state.fromDebtors);
  const method1 =
    location.state && typeof location.state.method1 === "string"
      ? location.state.method1
      : "credit";

  const handleBack = () => {
    if (fromSplit) {
      navigate("/frontshop/payment-types/split", {
        state: { total, fromDebtors },
      });
      return;
    }
    navigate("/frontshop/payment-types", { state: { total } });
  };

  const filteredProfiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return MOCK_PROFILES;
    return MOCK_PROFILES.filter((row) =>
      `${row.profileNumber} ${row.surname} ${row.address}`.toLowerCase().includes(q)
    );
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredProfiles.length / pageSize));

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProfiles.slice(start, start + pageSize);
  }, [filteredProfiles, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  useEffect(() => {
    if (location.state && typeof location.state.total === "number") {
      setTotal(location.state.total);
      return;
    }

    try {
      const raw = localStorage.getItem("frontshop_basket");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const subtotal = parsed.reduce(
            (sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 0),
            0
          );
          const tax = subtotal * 0.15;
          setTotal(subtotal + tax);
        }
      }
    } catch {
      // ignore storage parse errors
    }
  }, [location.state]);

  const handleView = (row) => {
    setShowModal(true);
  };

  const handleSelectDependent = (dependent) => {
    setShowModal(false);
    if (fromSplit) {
      navigate("/frontshop/payment-types/split/step2", {
        state: { total, method1, fromDebtors },
      });
      return;
    }
    navigate("/frontshop/payment-types/credit/balance", {
      state: { total, fromDebtors },
    });
  };

  const columns = [
    { header: "Profile Number", accessor: "profileNumber" },
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Address", accessor: "address" },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <Button
          type="button"
          variant="primary"
          className={styles.actionButton}
          onClick={() => handleView(row)}
        >
          View
        </Button>
      ),
    },
  ];

  const dependentColumns = [
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Role", accessor: "role" },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <Button
          type="button"
          variant="primary"
          className={styles.selectButton}
          onClick={() => handleSelectDependent(row)}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />

      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.filterCard}>
            <span className={styles.filterLabel}>Filter</span>
            <div className={styles.searchWrapper}>
              <Search size={16} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <section className={styles.tableSection}>
            <Table columns={columns} data={pageData} />

            {totalPages > 1 && (
              <div className={styles.paginationWrap}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </section>
        </div>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className={styles.modalInner}>
            <Table columns={dependentColumns} data={MOCK_DEPENDENTS} />
          </div>
        </Modal>
      )}

      {showConfirm && (
        <ConfirmPaymentModal
          total={total}
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false);
            setShowComplete(true);
          }}
        />
      )}

      {showComplete && (
        <PaymentCompleteModal
          amountPaid={total}
          paymentMethod="Credit"
          discountApplied={0}
          onNewSale={() => {
            setShowComplete(false);
            navigate("/frontshop");
          }}
          onClose={() => setShowComplete(false)}
        />
      )}
    </div>
  );
}
